import React, { useState, useEffect } from 'react';
import { Save, Loader2, Clock, Briefcase, TrendingUp, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { capacityApi } from '../services';

/**
 * Capacity Settings - User profile settings for resource planning
 * Allows lawyers to set their availability, practice areas, etc.
 */
const CapacitySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    weekly_capacity_hours: 40,
    practice_area_tags: [],
    seniority: 'MEDIOR',
    takes_marketplace_cases: true,
    max_concurrent_cases: 10,
    preferred_case_complexity: ['SIMPLE', 'COMPLEX']
  });
  const [newTag, setNewTag] = useState('');

  const practiceAreas = [
    'Arbeidsrett',
    'Eiendomsrett',
    'Familirett',
    'Selskapsrett',
    'Kontraktsrett',
    'Inkasso',
    'Forbrukerrett',
    'Skatterett',
    'Erstatningsrett',
    'Straffesrett'
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await capacityApi.getMyCapacity();
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch capacity settings:', error);
      toast.error('Kunne ikke laste innstillinger');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await capacityApi.updateMyCapacity(settings);
      toast.success('Kapasitetsinnstillinger lagret!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Kunne ikke lagre innstillinger');
    } finally {
      setSaving(false);
    }
  };

  const addPracticeArea = (area) => {
    if (!settings.practice_area_tags.includes(area)) {
      setSettings({
        ...settings,
        practice_area_tags: [...settings.practice_area_tags, area]
      });
    }
  };

  const removePracticeArea = (area) => {
    setSettings({
      ...settings,
      practice_area_tags: settings.practice_area_tags.filter(a => a !== area)
    });
  };

  const addCustomTag = () => {
    if (newTag.trim() && !settings.practice_area_tags.includes(newTag.trim())) {
      setSettings({
        ...settings,
        practice_area_tags: [...settings.practice_area_tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const toggleComplexity = (complexity) => {
    const current = settings.preferred_case_complexity || [];
    if (current.includes(complexity)) {
      setSettings({
        ...settings,
        preferred_case_complexity: current.filter(c => c !== complexity)
      });
    } else {
      setSettings({
        ...settings,
        preferred_case_complexity: [...current, complexity]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Kapasitetsinnstillinger</h2>
        <p className="text-sm text-gray-600">
          Angi din tilgjengelighet og preferanser for automatisk saksfordeling
        </p>
      </div>

      {/* Weekly Capacity */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-gray-900" />
          <h3 className="text-base font-semibold text-gray-900">Ukentlig kapasitet</h3>
        </div>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timer per uke
            </label>
            <input
              type="number"
              min="1"
              max="80"
              value={settings.weekly_capacity_hours}
              onChange={(e) => setSettings({...settings, weekly_capacity_hours: parseFloat(e.target.value)})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Standard: 40 timer per uke
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maks antall samtidige saker
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={settings.max_concurrent_cases}
              onChange={(e) => setSettings({...settings, max_concurrent_cases: parseInt(e.target.value)})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Anbefalt: 5-15 saker avhengig av kompleksitet
            </p>
          </div>
        </div>
      </div>

      {/* Seniority */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-gray-900" />
          <h3 className="text-base font-semibold text-gray-900">Seniority</h3>
        </div>
        
        <div className="grid grid-cols-4 gap-3">
          {['JUNIOR', 'MEDIOR', 'SENIOR', 'PARTNER'].map((level) => (
            <button
              key={level}
              onClick={() => setSettings({...settings, seniority: level})}
              className={`p-3 rounded-lg border-2 transition-all ${
                settings.seniority === level
                  ? 'border-gray-500 bg-gray-50 text-indigo-900'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium">{level}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Practice Areas */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Briefcase className="w-5 h-5 text-gray-900" />
          <h3 className="text-base font-semibold text-gray-900">Fagområder</h3>
        </div>
        
        <div className="space-y-4">
          {/* Selected Tags */}
          {settings.practice_area_tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dine fagområder
              </label>
              <div className="flex flex-wrap gap-2">
                {settings.practice_area_tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium flex items-center gap-1"
                  >
                    {tag}
                    <button
                      onClick={() => removePracticeArea(tag)}
                      className="ml-1 hover:text-indigo-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Quick Add */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Legg til fagområde
            </label>
            <div className="flex flex-wrap gap-2">
              {practiceAreas
                .filter(area => !settings.practice_area_tags.includes(area))
                .map((area) => (
                  <button
                    key={area}
                    onClick={() => addPracticeArea(area)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    + {area}
                  </button>
                ))}
            </div>
          </div>

          {/* Custom Tag */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Annet fagområde
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomTag()}
                placeholder="Skriv inn fagområde"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
              />
              <button
                onClick={addCustomTag}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
              >
                Legg til
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="w-5 h-5 text-gray-900" />
          <h3 className="text-base font-semibold text-gray-900">Preferanser</h3>
        </div>
        
        <div className="space-y-4">
          {/* Marketplace Cases */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900">Marketplace-saker</p>
              <p className="text-xs text-gray-600">Ta imot saker fra marketplace</p>
            </div>
            <button
              onClick={() => setSettings({...settings, takes_marketplace_cases: !settings.takes_marketplace_cases})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.takes_marketplace_cases ? 'bg-gray-900' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.takes_marketplace_cases ? 'trangray-x-6' : 'trangray-x-1'
                }`}
              />
            </button>
          </div>

          {/* Case Complexity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foretrukket kompleksitet
            </label>
            <div className="flex gap-3">
              {['SIMPLE', 'COMPLEX'].map((complexity) => (
                <button
                  key={complexity}
                  onClick={() => toggleComplexity(complexity)}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    (settings.preferred_case_complexity || []).includes(complexity)
                      ? 'border-gray-500 bg-gray-50 text-indigo-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium">
                    {complexity === 'SIMPLE' ? 'Enkle saker' : 'Komplekse saker'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Lagrer...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Lagre innstillinger
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CapacitySettings;
